import { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl';
import { WELL_STATUS_COLOR_MATCH } from 'components/well-page-content/subsurface-explorer/scoped-layers-constants';

/**
 * Renders the MVT layers for one Subsurface Explorer well category (Nearby or
 * Offset) on the inset map (INS-8658 / INS-9182).
 *
 * The backend stamps a tile-URL template per category carrying literal
 * `{geo_attribute}/{z}/{x}/{y}` placeholders. We substitute `{geo_attribute}`
 * per layer (survey / stick lines), leaving `{z}/{x}/{y}` for Mapbox. Tiles come
 * from the same ES `_mvt` generator as `wells_v2`, so the MVT layer name is
 * `hits`.
 *
 * Per Product: wells keep their status-based colors and are differentiated by a
 * thicker `lineWidth` (not a distinct color). Only the wellbore lines are drawn
 * — surface- and bottom-hole points (circles) are intentionally omitted.
 */
const GEO_ATTRIBUTES = ['survey', 'stick'] as const;

// ES `_mvt` emits the feature layer as `hits` (same as wells_v2 / dca tiles).
const SOURCE_LAYER = 'hits';

// Status colors come back from the constants module as a Mapbox match
// expression; react-map-gl's paint types want a string, so cast at the edge.
const STATUS_COLOR = WELL_STATUS_COLOR_MATCH as unknown as string;

interface ScopedWellsTileLayersProps {
  /** Unique id prefix per category, e.g. `nearby-wells` / `offset-wells`. */
  idPrefix: string;
  /** Server-stamped template with `{geo_attribute}/{z}/{x}/{y}` placeholders. */
  urlTemplate: string;
  /** Line width for this category — thicker = more emphasis (status color kept). */
  lineWidth: number;
  /** When set, appended as `search_radius_ft` so tiles match the inset radius. */
  searchRadiusFt?: number;
}

export const ScopedWellsTileLayers = ({
  idPrefix,
  urlTemplate,
  lineWidth,
  searchRadiusFt,
}: ScopedWellsTileLayersProps) => {
  const tilesByGeo = useMemo(() => {
    const withRadius = (url: string) => {
      if (!searchRadiusFt) return url;
      const sep = url.includes('?') ? '&' : '?';
      return `${url}${sep}search_radius_ft=${searchRadiusFt}`;
    };

    return Object.fromEntries(
      GEO_ATTRIBUTES.map((attr) => {
        const path = urlTemplate.replace('{geo_attribute}', attr);
        const abs = path.startsWith('http')
          ? path
          : `${window.location.origin}${path}`;
        return [attr, [withRadius(abs)]];
      })
    ) as Record<(typeof GEO_ATTRIBUTES)[number], string[]>;
  }, [urlTemplate, searchRadiusFt]);

  return (
    <>
      <Source id={`${idPrefix}-survey`} type="vector" tiles={tilesByGeo.survey}>
        <Layer
          id={`${idPrefix}-survey-lines`}
          type="line"
          source-layer={SOURCE_LAYER}
          paint={{
            'line-color': STATUS_COLOR,
            'line-width': lineWidth,
            'line-opacity': 0.95,
          }}
        />
      </Source>

      {/* Sticks are the fallback line for wells without a survey. */}
      <Source id={`${idPrefix}-stick`} type="vector" tiles={tilesByGeo.stick}>
        <Layer
          id={`${idPrefix}-stick-lines`}
          type="line"
          source-layer={SOURCE_LAYER}
          filter={['!', ['has', 'survey']]}
          paint={{
            'line-color': STATUS_COLOR,
            'line-width': lineWidth,
            'line-opacity': 0.8,
            'line-dasharray': [6, 2],
          }}
        />
      </Source>
    </>
  );
};
